import React, { useMemo } from 'react';
import LotValueTile from './LotValueTile';
import { computeRiskStats } from '../../utils/inventoryView';
import { AGING_BG_BY_ID, AGING_COLORS_BY_ID } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import './SummaryStrip.css';

// The dealership's aging buckets with today's colours; `bucket` is the id
// the filter holds.
const agingTiles = (buckets) => buckets.map((b) => ({
    key: b.key, label: b.tileLabel, bucket: b.id, color: AGING_COLORS_BY_ID[b.id], bg: AGING_BG_BY_ID[b.id],
}));

// RiskTier values come from the backend ("Red" | "Yellow" | "Green").
const RISK_TIERS = [
    { key: 'Red', label: 'High', color: '#b91c1c', bg: '#fee2e2' },
    { key: 'Yellow', label: 'Medium', color: '#b45309', bg: '#fef3c7' },
    { key: 'Green', label: 'Low', color: '#15803d', bg: '#dcfce7' },
];

function SummaryStrip({ stats, data, onAgingClick, activeAgingBuckets = [], onRiskTierClick, activeRiskTier }) {
    const riskStats = useMemo(() => computeRiskStats(data, RISK_TIERS), [data]);
    const { aging } = useDealershipSettings();

    return (
        <div className="summary-strip">
            {/* Left: Inventory Aging */}
            <div className="summary-panel aging-panel">
                <div className="panel-header">
                    <h3>Inventory Aging</h3>
                    <span className="panel-total">{stats.total.toLocaleString('en-US')}</span>
                    <span className="panel-total-label">units</span>
                </div>
                <div className="aging-grid">
                    {agingTiles(aging.buckets).map(b => {
                        const count = stats[b.key];
                        const pct = stats.total > 0 ? ((count / stats.total) * 100).toFixed(1) : '0.0';
                        const isActive = activeAgingBuckets.includes(b.bucket);
                        return (
                            <button
                                key={b.key}
                                className={`summary-item ${isActive ? 'active' : ''}`}
                                onClick={() => onAgingClick(b.bucket)}
                                style={{ '--item-color': b.color, '--item-bg': b.bg }}
                            >
                                <span className="item-dot" style={{ backgroundColor: b.color }}></span>
                                <span className="item-count">{count.toLocaleString('en-US')}</span>
                                <span className="item-label">{b.label}</span>
                                <span className="item-pct">{pct}%</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Right: Sell-Speed Risk — mirrors the aging tile (clickable buckets) */}
            {riskStats.total > 0 && (
                <div className="summary-panel risk-panel">
                    <div className="panel-header">
                        <h3>Sell-Speed Risk</h3>
                        <span className="panel-meta">
                            {riskStats.total.toLocaleString('en-US')} scored
                            {riskStats.highCount > 0 && (
                                <> &middot; <strong style={{ color: '#b91c1c' }}>{riskStats.highCount.toLocaleString('en-US')} at high risk</strong></>
                            )}
                        </span>
                    </div>
                    <div className="risk-tier-grid">
                        {riskStats.tiers.map(t => {
                            const isActive = activeRiskTier === t.key;
                            return (
                                <button
                                    key={t.key}
                                    className={`summary-item ${isActive ? 'active' : ''}`}
                                    onClick={() => onRiskTierClick(isActive ? 'All' : t.key)}
                                    style={{ '--item-color': t.color, '--item-bg': t.bg }}
                                >
                                    <span className="item-dot" style={{ backgroundColor: t.color }}></span>
                                    <span className="item-count">{t.count.toLocaleString('en-US')}</span>
                                    <span className="item-label">{t.label}</span>
                                    <span className="item-pct">{t.pct}%</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Lot value: asking vs AI value over the same filtered units */}
            <LotValueTile units={data} />
        </div>
    );
}

export default SummaryStrip;
